/**
 * ==== STANDALONE:SHARED:START ====
 * Feature: Core API client with cookie-based authentication
 * Tier: Infrastructure (shared)
 * Dependencies: None (vanilla fetch)
 * Generalization: Remove RB portal redirect paths (store/mill/portal/club),
 *   replace with generic auth redirect. Core cookie auth logic is portable.
 * ============================================
 *
 * API Client for Red Bridge Farm Feed Co.
 *
 * Provides fetch wrapper with cookie-based authentication.
 */

// Normalize API_BASE - remove trailing /api if present to avoid double /api/api
// Callers should include /api/ prefix in their endpoints
// Sanitize: strip trailing dots (DNS root) and trailing slashes from env var
const API_BASE_RAW = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\.+\/*$/, '').replace(/\/+$/, '');
const API_BASE = API_BASE_RAW.replace(/\/api\/?$/, '');

interface FetchOptions extends RequestInit {
  skipAuth?: boolean;
}

/**
 * Make an authenticated API request using cookies
 */
async function apiClient<T = unknown>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const { skipAuth, ...fetchOptions } = options;

  // Set default headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers as Record<string, string>),
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...fetchOptions,
    credentials: 'include', // Send cookies with requests
    headers,
  });

  // Handle 401 - session expired
  if (response.status === 401 && !skipAuth) {
    // Skip redirect for auth-check endpoints — 401 is expected (means "not logged in")
    if (endpoint.includes('/me/') || endpoint.includes('/my-locations/') || endpoint.includes('/auth/login') || endpoint.includes('/token/')) {
      throw new Error('Session expired');
    }

    const pathname = window.location.pathname;

    // Don't redirect if already on any login page
    if (pathname.includes('/login')) {
      throw new Error('Session expired');
    }

    // Redirect store routes to store login
    if (pathname.startsWith('/store')) {
      localStorage.removeItem('rbf_user_data');
      localStorage.removeItem('user');
      window.location.href = '/store/login';
      throw new Error('Session expired');
    }

    // Redirect mill routes to mill login
    if (pathname.startsWith('/mill')) {
      localStorage.removeItem('rbf_user_data');
      localStorage.removeItem('user');
      window.location.href = '/mill/login';
      throw new Error('Session expired');
    }

    // Don't redirect from portal, club routes - those have their own handlers
    if (pathname.startsWith('/portal') || pathname.startsWith('/club')) {
      throw new Error('Session expired');
    }

    // Admin routes - redirect to /login
    localStorage.removeItem('rbf_user_data');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || error.message || 'Request failed');
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

export default apiClient;

// ===== CONVENIENCE METHODS =====

export const api = {
  get: <T = unknown>(endpoint: string) =>
    apiClient<T>(endpoint, { method: 'GET' }),

  post: <T = unknown>(endpoint: string, data?: unknown) =>
    apiClient<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T = unknown>(endpoint: string, data?: unknown) =>
    apiClient<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T = unknown>(endpoint: string, data?: unknown) =>
    apiClient<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T = unknown>(endpoint: string) =>
    apiClient<T>(endpoint, { method: 'DELETE' }),
};

// ===== FILE UPLOAD =====

export const uploadFile = async <T = unknown>(
  endpoint: string,
  file: File,
  fieldName: string = 'file',
  additionalData?: Record<string, string>
): Promise<T> => {
  const formData = new FormData();
  formData.append(fieldName, file);

  if (additionalData) {
    Object.entries(additionalData).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    credentials: 'include', // Send cookies with requests
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || error.message || 'Upload failed');
  }

  return response.json();
};
// ==== STANDALONE:SHARED:END ====
